import { useState } from "react";
import { motion } from "framer-motion";
import { Gift, Users, DollarSign, ArrowRight, Check, Send, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReferralPage = () => {
  const [formData, setFormData] = useState({
    referrer_name: "",
    referrer_email: "",
    referrer_company: "",
    referee_name: "",
    referee_email: "",
    referee_company: "",
    referee_phone: "",
    notes: ""
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/referral`, formData);
      toast.success("Referral submitted successfully! We'll be in touch soon.");
      setFormData({
        referrer_name: "",
        referrer_email: "",
        referrer_company: "",
        referee_name: "",
        referee_email: "",
        referee_company: "",
        referee_phone: "",
        notes: ""
      });
    } catch (error) {
      toast.error("Failed to submit referral. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    {
      icon: DollarSign,
      title: "50% Credit Bonus",
      description: "Get 50% credit on your referee's first recharge"
    },
    {
      icon: Users,
      title: "No Limit",
      description: "Refer as many contacts as you want"
    },
    {
      icon: Gift,
      title: "Instant Rewards",
      description: "Credits added after referee's first payment"
    }
  ];

  return (
    <div data-testid="referral-page">
      {/* Hero */}
      <section className="bg-slate-900 text-white py-20 noise-bg" data-testid="referral-hero">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="inline-flex items-center gap-2 bg-brand-500/20 text-brand-300 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Gift className="w-4 h-4" />
              Referral Program
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-4">Earn 50% Credit</h1>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              Refer your contacts to VoipHub and earn 50% credit when they make their first recharge.
            </p>
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-light" data-testid="how-it-works">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Benefits */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                How It Works
              </h2>
              
              <div className="space-y-8 mb-8">
                {[
                  { step: "1", title: "Submit Referral", desc: "Fill out the form with your contact's details" },
                  { step: "2", title: "We Reach Out", desc: "Our team contacts your referral" },
                  { step: "3", title: "They Sign Up", desc: "Your referral creates an account and recharges" },
                  { step: "4", title: "You Earn", desc: "Get 50% of their first recharge as credit" }
                ].map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.1 }}
                    viewport={{ once: true }}
                    className="flex gap-4"
                  >
                    <div className="w-10 h-10 bg-brand-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                      {item.step}
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{item.title}</h3>
                      <p className="text-slate-600">{item.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
              
              {/* Benefits Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {benefits.map((benefit, i) => (
                  <div key={i} className="bg-slate-50 rounded-xl p-4 text-center">
                    <benefit.icon className="w-8 h-8 text-brand-500 mx-auto mb-2" />
                    <h4 className="font-semibold text-slate-900 text-sm">{benefit.title}</h4>
                    <p className="text-slate-500 text-xs mt-1">{benefit.description}</p>
                  </div>
                ))}
              </div>
              
              {/* Example */}
              <div className="mt-8 bg-brand-25 border border-brand-100 rounded-xl p-6">
                <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-brand-500" />
                  Example
                </h4>
                <p className="text-slate-700">
                  If your referral recharges with <strong>€100</strong>, you receive <strong>€50</strong> credit in your account!
                </p>
                <p className="text-sm text-slate-500 mt-2">
                  Minimum recharge requirement: 100 USD/EUR
                </p>
              </div>
            </motion.div>

            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl shadow-xl p-8 border border-slate-200"
            >
              <h3 className="text-2xl font-bold text-slate-900 mb-6">Submit a Referral</h3>
              
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="referral-form">
                {/* Your Info */}
                <div className="space-y-4">
                  <h4 className="font-medium text-slate-700 text-sm uppercase tracking-wide">Your Information</h4>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="referrer_name">Your Name *</Label>
                      <Input
                        id="referrer_name"
                        name="referrer_name"
                        value={formData.referrer_name}
                        onChange={handleChange}
                        required
                        className="mt-1"
                        data-testid="referrer-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="referrer_email">Your Email *</Label>
                      <Input
                        id="referrer_email"
                        name="referrer_email"
                        type="email"
                        value={formData.referrer_email}
                        onChange={handleChange}
                        required
                        className="mt-1"
                        data-testid="referrer-email-input"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="referrer_company">Your Company</Label>
                    <Input
                      id="referrer_company"
                      name="referrer_company"
                      value={formData.referrer_company}
                      onChange={handleChange}
                      className="mt-1"
                    />
                  </div>
                </div>

                <div className="border-t border-slate-200" />

                {/* Referee Info */}
                <div className="space-y-4">
                  <h4 className="font-medium text-slate-700 text-sm uppercase tracking-wide">Referral's Information</h4>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="referee_name">Contact Name *</Label>
                      <Input
                        id="referee_name"
                        name="referee_name"
                        value={formData.referee_name}
                        onChange={handleChange}
                        required
                        className="mt-1"
                        data-testid="referee-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="referee_email">Contact Email *</Label>
                      <Input
                        id="referee_email"
                        name="referee_email"
                        type="email"
                        value={formData.referee_email}
                        onChange={handleChange}
                        required
                        className="mt-1"
                        data-testid="referee-email-input"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="referee_company">Contact Company</Label>
                      <Input
                        id="referee_company"
                        name="referee_company"
                        value={formData.referee_company}
                        onChange={handleChange}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="referee_phone">Contact Phone</Label>
                      <Input
                        id="referee_phone"
                        name="referee_phone"
                        value={formData.referee_phone}
                        onChange={handleChange}
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <Label htmlFor="notes">Additional Notes</Label>
                  <Textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    placeholder="Any additional information about the referral..."
                    className="mt-1"
                    rows={3}
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-brand-500 hover:bg-brand-600 text-white rounded-full py-6 font-semibold"
                  disabled={loading}
                  data-testid="referral-submit-btn"
                >
                  {loading ? (
                    "Submitting..."
                  ) : (
                    <>
                      <Send className="mr-2 w-4 h-4" />
                      Submit Referral
                    </>
                  )}
                </Button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-alt" data-testid="referral-faq">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 text-center mb-10">
              Frequently Asked Questions
            </h2>
            
            <div className="space-y-4">
              {[
                { q: "When do I receive my referral credit?", a: "Credits are added to your account within 24 hours after your referral makes their first recharge of at least 100 USD/EUR." },
                { q: "Is there a limit to how many people I can refer?", a: "No! There's no limit. Refer as many contacts as you want and earn 50% credit on each one's first recharge." },
                { q: "Can I refer someone who already has an account?", a: "No, the referral program is only for new customers who haven't signed up before." },
                { q: "How long is the referral credit valid?", a: "Referral credits never expire and can be used for any of our services." }
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-xl p-6 shadow-sm"
                >
                  <h4 className="font-semibold text-slate-900 mb-2">{item.q}</h4>
                  <p className="text-slate-600">{item.a}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ReferralPage;
